import { useMemo } from 'react'
import { ChargeCategoryFragment } from 'src/api/api.generated'
import { ReservedResourceF } from '../../../../../types/reservation-detail.types'
import { Trans, useTranslation } from 'react-i18next'
import './../../../../../../../sinapsi/style/price-calendar.sinapsi.css'

type RRBulkEditChargeCategoryGridFooterCellProps = {
  dayChargeCategories: { rr: ReservedResourceF; cc?: ChargeCategoryFragment }[]
}

type MealGuests = {
  adults: number
  children: number
}

export default function RRBulkEditChargeCategoryGridFooterCell(props: RRBulkEditChargeCategoryGridFooterCellProps) {
  const { dayChargeCategories } = props
  const { t } = useTranslation()

  const reduceGuestByMeal = (mealCondition: (chargeCategory: ChargeCategoryFragment) => boolean): MealGuests => {
    return dayChargeCategories.reduce(
      (prev, curr) => {
        if (curr.cc && mealCondition(curr.cc)) {
          return {
            adults: prev.adults + curr.rr.adults,
            children: prev.children + curr.rr.children,
          }
        }
        return prev
      },
      { adults: 0, children: 0 },
    )
  }

  const noBreakfastGuests = useMemo(() => {
    const meal = reduceGuestByMeal((cc) => !cc.breakfastIncluded && !cc.dinnerIncluded && !cc.lunchIncluded)
    if (meal.children === 0) {
      return <Trans i18nKey="pms.booking.reservation.rr_bulk_edit_cc_drawer.footer.no_breakfast" values={{ total: meal.adults }} />
    } else {
      return (
        <Trans
          i18nKey="pms.booking.reservation.rr_bulk_edit_cc_drawer.footer.no_breakfast_with_children"
          values={{ total: meal.adults + meal.children, adults: meal.adults, children: meal.children }}
        />
      )
    }
  }, [dayChargeCategories])

  const breakfastGuests = useMemo(() => {
    const meal = reduceGuestByMeal((cc) => cc.breakfastIncluded)
    if (meal.children === 0) {
      return <Trans i18nKey="pms.booking.reservation.rr_bulk_edit_cc_drawer.footer.include_breakfast" values={{ total: meal.adults }} />
    } else {
      return (
        <Trans
          i18nKey="pms.booking.reservation.rr_bulk_edit_cc_drawer.footer.include_breakfast_with_children"
          values={{ total: meal.adults + meal.children, adults: meal.adults, children: meal.children }}
        />
      )
    }
  }, [dayChargeCategories])

  const lunchGuests = useMemo(() => {
    const meal = reduceGuestByMeal((cc) => cc.lunchIncluded)
    if (meal.children === 0) {
      return <Trans i18nKey="pms.booking.reservation.rr_bulk_edit_cc_drawer.footer.include_lunch" values={{ total: meal.adults }} />
    } else {
      return (
        <Trans
          i18nKey="pms.booking.reservation.rr_bulk_edit_cc_drawer.footer.include_lunch_with_children"
          values={{ total: meal.adults + meal.children, adults: meal.adults, children: meal.children }}
        />
      )
    }
  }, [dayChargeCategories])

  const dinnerGuests = useMemo(() => {
    const meal = reduceGuestByMeal((cc) => cc.dinnerIncluded)
    if (meal.children === 0) {
      return <Trans i18nKey="pms.booking.reservation.rr_bulk_edit_cc_drawer.footer.include_dinner" values={{ total: meal.adults }} />
    } else {
      return (
        <Trans
          i18nKey="pms.booking.reservation.rr_bulk_edit_cc_drawer.footer.include_dinner_with_children"
          values={{ total: meal.adults + meal.children, adults: meal.adults, children: meal.children }}
        />
      )
    }
  }, [dayChargeCategories])

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        marginTop: '4px',
        marginBottom: '4px',
      }}
    >
      <p className="small-font" style={{ margin: '0px' }}>
        {noBreakfastGuests}
      </p>
      <p className="small-font" style={{ margin: '0px' }}>
        {breakfastGuests}
      </p>
      <p className="small-font" style={{ margin: '0px' }}>
        {lunchGuests}
      </p>
      <p className="small-font" style={{ margin: '0px' }}>
        {dinnerGuests}
      </p>
    </div>
  )
}
